class MapBlock {
  constructor(section) {
    this.section = section;
    this.canvas = section.querySelector('.map__canvas');
    this.search = section.querySelector('[data-map-search]');
    this.searchInput = section.querySelector('[data-map-search-input]');
    this.searchClear = section.querySelector('[data-map-search-clear]');
    this.searchResults = section.querySelector('[data-map-search-results]');
    this.tabs = Array.from(section.querySelectorAll('[data-map-filter]'));
    this.map = null;
    this.clusterer = null;
    this.placemarks = [];
    this.placemarkByKey = new Map();
    this.points = this.parsePoints();
    this.i18n = this.parseI18n();
    this.activeFilter = 'all';
    this.apiCheckInterval = null;
    this.attempts = 0;
    this.maxAttempts = 50;
    this.scrollZoomEnableDelay = 2000;
    this.scrollZoomTimer = null;
    this.isScrollZoomEnabled = false;

    if (!this.canvas) {
      return;
    }

    this.bindTabs();
    this.bindSearch();
    this.init();
  }

  parsePoints() {
    const raw = this.getJsonSource('[data-map-points-json]', this.canvas?.dataset.mapPoints || '[]');

    try {
      const points = JSON.parse(raw);
      if (!Array.isArray(points)) {
        return [];
      }

      return points.filter((point) => (
        Number.isFinite(Number(point.lat))
        && Number.isFinite(Number(point.lng))
      ));
    } catch (error) {
      return [];
    }
  }

  parseI18n() {
    const raw = this.getJsonSource('[data-map-i18n-json]', this.canvas?.dataset.mapI18n || '{}');

    try {
      const i18n = JSON.parse(raw);
      return (i18n && typeof i18n === 'object') ? i18n : {};
    } catch (error) {
      return {};
    }
  }

  getJsonSource(selector, fallback = '') {
    const script = this.section?.querySelector(selector);
    if (script && typeof script.textContent === 'string' && script.textContent.trim().length) {
      return script.textContent;
    }

    return fallback;
  }

  bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const filter = tab.dataset.mapFilter || 'all';
        this.activeFilter = filter;
        this.updateTabsState();
        this.renderPoints();
        this.renderSearchResults();
      });
    });
  }

  bindSearch() {
    if (!this.searchInput || !this.searchResults) {
      return;
    }

    this.searchInput.addEventListener('input', () => {
      this.updateSearchClearState();
      this.renderSearchResults();
    });

    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        this.searchInput.value = '';
        this.updateSearchClearState();
        this.renderSearchResults();
        this.searchInput.focus();
      });
    }

    this.searchResults.addEventListener('click', (event) => {
      const button = event.target.closest('[data-map-search-key]');
      if (!button) {
        return;
      }

      const key = button.dataset.mapSearchKey || '';
      this.clearPlacemarkActiveStates(key);
      setTimeout(() => {
        this.focusPointBySearchKey(key, 'active');
      }, 0);
      this.searchResults.classList.remove('is-open');
    });

    document.addEventListener('click', (event) => {
      if (!this.search?.contains(event.target)) {
        this.searchResults.classList.remove('is-open');
      }
    });

    this.updateSearchClearState();
  }

  focusPointBySearchKey(key, mode = 'active') {
    const placemark = this.placemarkByKey.get(key);
    const point = this.getFilteredPoints().find((item) => this.getPointKey(item) === key);

    if (!placemark || !point || !this.map) {
      return;
    }

    this.map.setCenter([Number(point.lat), Number(point.lng)], 19, { duration: 200 });

    const isHoverMode = mode === 'hover';
    const isActiveMode = mode === 'active';
    const defaultIconHref = point.icon || '';
    const hoverIconHref = point.hover_icon || defaultIconHref;
    const activeIconHref = this.getActiveIconHref(point) || hoverIconHref || defaultIconHref;
    const defaultIconSize = [32, 32];
    const defaultIconOffset = [-16, -32];
    const activeIconSize = [42, 46];
    const activeIconOffset = [-21, -46];

    if (isHoverMode) {
      placemark.properties.set('balloonContent', this.buildBalloonContent(point, false));
      placemark.properties.set('isHoverBalloonMode', true);
      placemark.properties.set('isActive', false);
      placemark.options.set('iconImageHref', hoverIconHref);
      placemark.options.set('iconImageSize', activeIconSize);
      placemark.options.set('iconImageOffset', activeIconOffset);
      placemark.balloon.open();
      return;
    }

    if (isActiveMode) {
      placemark.properties.set('balloonContent', this.buildBalloonContent(point, true));
      placemark.properties.set('isHoverBalloonMode', false);
      placemark.properties.set('isActive', true);
      placemark.options.set('iconImageHref', activeIconHref);
      placemark.options.set('iconImageSize', activeIconSize);
      placemark.options.set('iconImageOffset', activeIconOffset);
      let isOpened = false;
      const openActiveBalloon = () => {
        if (isOpened) {
          return;
        }

        isOpened = true;
        this.map.events.remove('actionend', onActionEnd);
        placemark.balloon.open();
      };
      const onActionEnd = () => {
        openActiveBalloon();
      };

      this.map.events.add('actionend', onActionEnd);
      setTimeout(() => {
        openActiveBalloon();
      }, 260);
      return;
    }

    placemark.properties.set('isHoverBalloonMode', false);
    placemark.properties.set('isActive', false);
    placemark.options.set('iconImageHref', defaultIconHref);
    placemark.options.set('iconImageSize', defaultIconSize);
    placemark.options.set('iconImageOffset', defaultIconOffset);
  }

  clearPlacemarkActiveStates(exceptKey = '') {
    const defaultIconSize = [32, 32];
    const defaultIconOffset = [-16, -32];

    this.getFilteredPoints().forEach((point) => {
      const key = this.getPointKey(point);
      if (key === exceptKey) {
        return;
      }

      const placemark = this.placemarkByKey.get(key);

      if (!placemark) {
        return;
      }

      placemark.properties.set('isActive', false);
      placemark.properties.set('isHoverBalloonMode', false);
      placemark.properties.set('isHovered', false);
      placemark.properties.set('balloonContent', this.buildBalloonContent(point, true));

      if (point.icon) {
        placemark.options.set('iconImageHref', point.icon);
        placemark.options.set('iconImageSize', defaultIconSize);
        placemark.options.set('iconImageOffset', defaultIconOffset);
      }

      if (placemark.balloon.isOpen()) {
        placemark.balloon.close();
      }
    });
  }

  updateTabsState() {
    this.tabs.forEach((tab) => {
      const isActive = (tab.dataset.mapFilter || 'all') === this.activeFilter;
      tab.classList.toggle('is-active', isActive);
    });
  }

  init() {
    if (typeof ymaps !== 'undefined') {
      this.createMap();
      return;
    }

    this.waitForApi();
  }

  waitForApi() {
    this.apiCheckInterval = setInterval(() => {
      this.attempts += 1;

      if (typeof ymaps !== 'undefined') {
        clearInterval(this.apiCheckInterval);
        this.createMap();
      } else if (this.attempts >= this.maxAttempts) {
        clearInterval(this.apiCheckInterval);
      }
    }, 100);
  }

  createMap() {
    ymaps.ready(() => {
      const firstPoint = this.points[0];
      const center = firstPoint
        ? [Number(firstPoint.lat), Number(firstPoint.lng)]
        : [53.902284, 27.561831];

      this.map = new ymaps.Map(this.canvas.id, {
        center,
        zoom: 12,
        controls: ['zoomControl'],
      });
      this.map.behaviors.disable('scrollZoom');
      this.bindDelayedScrollZoom();

      this.clusterer = new ymaps.Clusterer({
        groupByCoordinates: false,
        clusterDisableClickZoom: false,
        clusterOpenBalloonOnClick: true,
        clusterBalloonPanelMaxMapArea: 0,
      });
      this.map.geoObjects.add(this.clusterer);

      this.renderPoints();
    });
  }

  bindDelayedScrollZoom() {
    if (!this.canvas) {
      return;
    }

    this.canvas.addEventListener('mouseenter', () => {
      this.clearScrollZoomTimer();
      this.scrollZoomTimer = window.setTimeout(() => {
        this.toggleScrollZoom(true);
      }, this.scrollZoomEnableDelay);
    });

    this.canvas.addEventListener('mouseleave', () => {
      this.clearScrollZoomTimer();
      this.toggleScrollZoom(false);
    });
  }

  clearScrollZoomTimer() {
    if (this.scrollZoomTimer === null) {
      return;
    }

    clearTimeout(this.scrollZoomTimer);
    this.scrollZoomTimer = null;
  }

  toggleScrollZoom(isEnabled) {
    if (!this.map || this.isScrollZoomEnabled === isEnabled) {
      return;
    }

    if (isEnabled) {
      this.map.behaviors.enable('scrollZoom');
    } else {
      this.map.behaviors.disable('scrollZoom');
    }

    this.isScrollZoomEnabled = isEnabled;
  }

  getFilteredPoints() {
    if (this.activeFilter === 'all') {
      return this.points;
    }

    return this.points.filter((point) => point.type === this.activeFilter);
  }

  getPointTypeLabel(type) {
    if (type === 'kiosks') return this.i18n.point_type_kiosks || 'Киоск';
    if (type === 'non_isolated') return this.i18n.point_type_non_isolated || 'Неизолированные торговые объекты';
    if (type === 'branches') return this.i18n.point_type_branches || 'Филиалы «ТАБАК»';
    return '';
  }

  getPointKey(point) {
    return [
      point.type || '',
      point.number || '',
      point.description || '',
      Number(point.lat),
      Number(point.lng),
    ].join('|');
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  updateSearchClearState() {
    if (!this.searchClear || !this.searchInput) {
      return;
    }

    this.searchClear.classList.toggle('is-visible', Boolean(this.searchInput.value.trim().length));
  }

  buildBalloonContent(point, includeDescription = true) {
    const typeLabel = this.getPointTypeLabel(point.type);
    const type = typeLabel ? `<div class="map-balloon__type">${typeLabel}</div>` : '';
    const number = point.number ? `<strong class="map-balloon__number">№ ${point.number}</strong>` : '';
    const description = includeDescription && point.description
      ? `<div class="map-balloon__description">${point.description}</div>`
      : '';
    return `${type}${number}${description}`;
  }

  getDerivedActiveIconHref(iconHref) {
    if (!iconHref) {
      return '';
    }

    const [pathPart, query = ''] = String(iconHref).split('?');
    const match = pathPart.match(/^(.*)(\.[a-zA-Z0-9]+)$/);
    if (!match) {
      return '';
    }

    const basePath = match[1];
    const extension = match[2];
    const derived = `${basePath}-active${extension}`;
    return query ? `${derived}?${query}` : derived;
  }

  getActiveIconHref(point) {
    if (point.active_icon) {
      return point.active_icon;
    }

    return this.getDerivedActiveIconHref(point.icon);
  }

  createPlacemark(point) {
    const coords = [Number(point.lat), Number(point.lng)];
    const hasIcon = Boolean(point.icon);
    const defaultIconHref = point.icon;
    const hoverIconHref = point.hover_icon || defaultIconHref;
    const activeIconHref = this.getActiveIconHref(point);
    const hoverBalloonContent = this.buildBalloonContent(point, false);
    const activeBalloonContent = this.buildBalloonContent(point, true);
    const defaultIconSize = [32, 32];
    const defaultIconOffset = [-16, -32];
    const activeIconSize = [42, 46];
    const activeIconOffset = [-21, -46];
    const options = hasIcon
      ? {
        iconLayout: 'default#image',
        iconImageHref: defaultIconHref,
        iconImageSize: defaultIconSize,
        iconImageOffset: defaultIconOffset,
        openBalloonOnClick: false,
        hideIconOnBalloonOpen: false,
        balloonOffset: [85, 10],
      }
      : {
        preset: 'islands#blueIcon',
        openBalloonOnClick: false,
        hideIconOnBalloonOpen: false,
        balloonOffset: [85, 10],
      };

    const placemark = new ymaps.Placemark(
      coords,
      {
        balloonContent: activeBalloonContent,
      },
      options,
    );

    if (hasIcon && activeIconHref) {
      placemark.properties.set('isHovered', false);
      placemark.properties.set('isActive', false);
      placemark.properties.set('isHoverBalloonMode', false);

      placemark.events.add('mouseenter', () => {
        const isActive = Boolean(placemark.properties.get('isActive'));
        placemark.properties.set('isHovered', true);

        if (isActive) {
          return;
        }

        placemark.properties.set('isHoverBalloonMode', true);
        placemark.properties.set('balloonContent', hoverBalloonContent);

        if (placemark.balloon.isOpen()) {
          placemark.options.set('iconImageHref', hoverIconHref);
          placemark.options.set('iconImageSize', activeIconSize);
          placemark.options.set('iconImageOffset', activeIconOffset);
          return;
        }

        placemark.options.set('iconImageHref', hoverIconHref);
        placemark.options.set('iconImageSize', activeIconSize);
        placemark.options.set('iconImageOffset', activeIconOffset);
        placemark.balloon.open();
      });

      placemark.events.add('mouseleave', () => {
        const isActive = Boolean(placemark.properties.get('isActive'));
        placemark.properties.set('isHovered', false);

        if (isActive) {
          return;
        }

        placemark.properties.set('isHoverBalloonMode', false);
        placemark.properties.set('balloonContent', activeBalloonContent);

        if (placemark.balloon.isOpen()) {
          placemark.balloon.close();
        }

        placemark.options.set('iconImageHref', defaultIconHref);
        placemark.options.set('iconImageSize', defaultIconSize);
        placemark.options.set('iconImageOffset', defaultIconOffset);
      });

      placemark.events.add('click', () => {
        if (this.map) {
          this.map.setCenter(coords, 19, { duration: 200 });
        }
        placemark.properties.set('isActive', true);
        placemark.properties.set('isHoverBalloonMode', false);
        placemark.properties.set('balloonContent', activeBalloonContent);
        placemark.options.set('iconImageHref', activeIconHref);
        placemark.options.set('iconImageSize', activeIconSize);
        placemark.options.set('iconImageOffset', activeIconOffset);
        placemark.balloon.open();
      });

      placemark.events.add('balloonopen', () => {
        const isHoverBalloonMode = Boolean(placemark.properties.get('isHoverBalloonMode'));
        placemark.options.set('iconImageHref', isHoverBalloonMode ? hoverIconHref : activeIconHref);
        placemark.options.set('iconImageSize', activeIconSize);
        placemark.options.set('iconImageOffset', activeIconOffset);
      });

      placemark.events.add('balloonclose', () => {
        const isHovered = Boolean(placemark.properties.get('isHovered'));
        placemark.properties.set('isActive', false);
        placemark.options.set('iconImageHref', isHovered ? hoverIconHref : defaultIconHref);
        placemark.options.set('iconImageSize', isHovered ? activeIconSize : defaultIconSize);
        placemark.options.set('iconImageOffset', isHovered ? activeIconOffset : defaultIconOffset);
      });
    }

    return placemark;
  }

  clearPlacemarks() {
    if (this.clusterer) {
      this.clusterer.removeAll();
    }
    this.placemarks = [];
    this.placemarkByKey.clear();
  }

  renderSearchResults() {
    if (!this.searchInput || !this.searchResults) {
      return;
    }

    const query = this.searchInput.value.trim().toLowerCase();
    if (query.length < 1) {
      this.searchResults.innerHTML = '';
      this.searchResults.classList.remove('is-open');
      return;
    }

    const points = this.getFilteredPoints()
      .filter((point) => {
        const typeLabel = this.getPointTypeLabel(point.type).toLowerCase();
        return [
          typeLabel,
          String(point.number || '').toLowerCase(),
          String(point.description || '').toLowerCase(),
        ].some((value) => value.includes(query));
      })
      .slice(0, 20);

    if (!points.length) {
      const noResultsLine1 = this.escapeHtml(this.i18n.search_no_results_line_1 || 'По вашему запросу ничего не найдено');
      const noResultsLine2 = this.escapeHtml(this.i18n.search_no_results_line_2 || 'Скорректируйте запрос в поиске');
      this.searchResults.innerHTML = `<div class="map__search-empty">${noResultsLine1}<br>${noResultsLine2}</div>`;
      this.searchResults.classList.add('is-open');
      return;
    }

    this.searchResults.innerHTML = points.map((point) => `
      <button class="map__search-item" type="button" data-map-search-key="${this.escapeHtml(this.getPointKey(point))}">
        <span class="map__search-item-icon-wrap">
          ${point.icon ? `<img class="map__search-item-icon" src="${this.escapeHtml(point.icon)}" alt="" aria-hidden="true">` : ''}
        </span>
        <span class="map__search-item-content">
          <span class="map__search-item-type">${this.escapeHtml(this.getPointTypeLabel(point.type))}</span>
          <span class="map__search-item-number">${this.escapeHtml(point.number || '')}</span>
          <span class="map__search-item-description">${this.escapeHtml(point.description || '')}</span>
        </span>
      </button>
    `).join('');
    this.searchResults.classList.add('is-open');
  }

  fitToPoints(points) {
    if (!points.length) {
      return;
    }

    if (points.length === 1) {
      this.map.setCenter([Number(points[0].lat), Number(points[0].lng)], 14);
      return;
    }

    const lats = points.map((point) => Number(point.lat));
    const lngs = points.map((point) => Number(point.lng));

    this.map.setBounds(
      [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
      {
        checkZoomRange: true,
        zoomMargin: 40,
      },
    );
  }

  renderPoints() {
    if (!this.map) {
      return;
    }

    const points = this.getFilteredPoints();
    this.clearPlacemarks();

    points.forEach((point) => {
      const placemark = this.createPlacemark(point);
      this.placemarks.push(placemark);
      this.placemarkByKey.set(this.getPointKey(point), placemark);
    });
    if (this.clusterer) {
      this.clusterer.add(this.placemarks);
    }

    this.fitToPoints(points);
  }
}

document.querySelectorAll('.map').forEach((section) => {
  new MapBlock(section);
});
